import copy from 'clipboard-copy'
import { showErrorNotification, showInlineNotification } from '~/core/ui'
import { fetchPersonalRoomInvite, fetchPersonalRoom } from '~/services/room'
import { ACTION_TYPES } from '~/core/constants'
import { logInfo } from '~/core/logger'
import analytics from '~/services/analytics'
import api from '~/services/api'
import { translate } from '~/core/translate'
import { getFullNameFromUrl, getNameFromUrl } from '~/core/utils'
import envData from '~/core/env'
import { getPersonalRoomUrl } from '~/selectors/room'
import { getHasMeetings } from '~/selectors/app'

const { ROOM } = ACTION_TYPES

export const setPersonalRoom = room => ({
  type: ROOM.SET_PERSONAL_ROOM,
  payload: room
})

export const setCustomRoomName = value => ({
  type: ROOM.SET_CUSTOM_ROOM_NAME,
  payload: value
})

export const copyPersonalRoomInfo = () => async () => {
  logInfo({ __filename }, 'Copying personal room info')

  analytics.trackInitEvent(analytics.EVENT_NAMES.COPY_TO_CLIPBOARD)
  const invite = await fetchPersonalRoomInvite()

  if (!invite) {
    analytics.trackFailedEvent(analytics.EVENT_NAMES.COPY_TO_CLIPBOARD, {
      reason: 'Could not fetch invite'
    })
    showErrorNotification(translate('could_not_copy_invite'))
    return
  }

  try {
    await copy(invite)
    showInlineNotification(translate('meeting_info_copied'))
  } catch (err) {
    analytics.trackFailedEvent(analytics.EVENT_NAMES.COPY_TO_CLIPBOARD, {
      reason: err
    })
    showErrorNotification(translate('could_not_copy_invite'))
  }
}

export const copyPersonalRoomLink = () => async (dispatch, getState) => {
  logInfo({ __filename }, 'Copying personal room link')

  analytics.trackInitEvent(analytics.EVENT_NAMES.COPY_TO_CLIPBOARD)
  const url = getPersonalRoomUrl(getState())

  try {
    await copy(url)
  } catch (err) {
    analytics.trackFailedEvent(analytics.EVENT_NAMES.COPY_TO_CLIPBOARD, {
      reason: err
    })
    showErrorNotification(translate('could_not_copy_link'))
  }
}

export const copyCustomRoomLink = value => async () => {
  logInfo({ __filename }, 'Copying custom room link')

  analytics.trackInitEvent(analytics.EVENT_NAMES.COPY_TO_CLIPBOARD)
  const url = `${envData.webUrl}/${value}`

  try {
    await copy(url)
  } catch (err) {
    analytics.trackFailedEvent(analytics.EVENT_NAMES.COPY_TO_CLIPBOARD, {
      reason: err
    })
    showErrorNotification(translate('could_not_copy_link'))
  }
}

export const getPersonalRoom = () => async (dispatch, getState) => {
  logInfo({ __filename }, 'Retrieving personal room')

  const hasMeetings = getHasMeetings(getState())

  if (!hasMeetings) {
    return null
  }

  const resp = await fetchPersonalRoom()

  if (api.isErrorResponse(resp)) {
    showErrorNotification(translate('error_fetching_personal_room'))

    return null
  }

  const { url, passcode } = resp

  const fullName = getFullNameFromUrl(url)
  const name = getNameFromUrl(url)
  const room = { name, fullName, url, password: passcode || '' }

  dispatch(setPersonalRoom(room))

  return room
}

export const getMeetNowInvite = () => dispatch =>
  dispatch(getPersonalRoom())
    .then(room =>
      room
        ? translate('jitsi_meeting_invite_template', { url: room.url })
        : null
    )
    .catch(() => null)
